import { applyI18n } from '../i18n';

const DEFAULT_STORAGE_KEY = 'shareweb:files-browse-split';
const DEFAULT_TREE_FRACTION = 260 / (260 + 640);
const MIN_TREE_REM = 12.5; // ~200px
const MIN_CONTENT_REM = 18;
const NARROW_MQ = '(max-width: 960px)';

function remToPx(rem: number): number {
  const root = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
  return rem * (Number.isFinite(root) && root > 0 ? root : 16);
}

function readStoredFraction(storageKey: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw == null) return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

function writeStoredFraction(storageKey: string, fraction: number): void {
  try {
    localStorage.setItem(storageKey, String(fraction));
  } catch {
    // Ignore quota / private-mode failures.
  }
}

function clampFraction(
  fraction: number,
  availablePx: number,
  minTreePx: number,
  minContentPx: number,
): number {
  if (!(availablePx > 0) || availablePx <= minTreePx + minContentPx) {
    return fraction;
  }
  const minFrac = minTreePx / availablePx;
  const maxFrac = 1 - minContentPx / availablePx;
  return Math.min(Math.max(fraction, minFrac), maxFrac);
}

export interface BrowseSplitResizeOptions {
  storageKey?: string;
  ariaLabelKey?: string;
}

/**
 * Two-column browse split resize via an edge handle overlaid on the existing
 * border — no blank gutter column.
 */
export function bindBrowseSplitResize(
  split: HTMLElement,
  options: BrowseSplitResizeOptions = {},
): () => void {
  const storageKey = options.storageKey ?? DEFAULT_STORAGE_KEY;
  const ariaLabelKey = options.ariaLabelKey ?? 'files.browse.resizeGutter';

  split.classList.add('files-browse-split--resizable');
  split.style.position = split.style.position || 'relative';

  let handle = split.querySelector<HTMLButtonElement>('.files-browse-split__handle');
  if (!handle) {
    handle = document.createElement('button');
    handle.type = 'button';
    handle.className = 'files-browse-split__handle';
    handle.setAttribute('data-i18n-aria-label', ariaLabelKey);
    split.append(handle);
  }
  applyI18n(handle);

  let treeFraction = readStoredFraction(storageKey, DEFAULT_TREE_FRACTION);
  let dragging = false;
  const mq = window.matchMedia(NARROW_MQ);

  const applyColumns = (fraction: number, availablePx: number): void => {
    const minTree = remToPx(MIN_TREE_REM);
    const minContent = remToPx(MIN_CONTENT_REM);
    const clamped = clampFraction(fraction, availablePx, minTree, minContent);
    const treePx = Math.round(clamped * availablePx);
    split.style.gridTemplateColumns = `${treePx}px minmax(0, 1fr)`;
    handle!.style.left = `${treePx}px`;
  };

  const applyStored = (): void => {
    if (mq.matches) {
      split.style.gridTemplateColumns = '';
      return;
    }
    const available = split.getBoundingClientRect().width;
    const minTree = remToPx(MIN_TREE_REM);
    const minContent = remToPx(MIN_CONTENT_REM);
    if (available <= minTree + minContent) {
      return;
    }
    treeFraction = clampFraction(treeFraction, available, minTree, minContent);
    applyColumns(treeFraction, available);
  };

  const applyFromPointer = (clientX: number): void => {
    if (mq.matches) return;
    const rect = split.getBoundingClientRect();
    const available = rect.width;
    const minTree = remToPx(MIN_TREE_REM);
    const minContent = remToPx(MIN_CONTENT_REM);
    if (available <= minTree + minContent) return;
    const treePx = Math.min(Math.max(clientX - rect.left, minTree), available - minContent);
    treeFraction = treePx / available;
    applyColumns(treeFraction, available);
  };

  applyStored();

  const resizeObserver = new ResizeObserver(() => {
    if (dragging) return;
    applyStored();
  });
  resizeObserver.observe(split);

  const onPointerDown = (event: PointerEvent) => {
    if (mq.matches) return;
    event.preventDefault();
    handle!.setPointerCapture(event.pointerId);
    dragging = true;
    split.classList.add('files-browse-split--dragging');
    applyFromPointer(event.clientX);
  };
  const onPointerMove = (event: PointerEvent) => {
    if (!handle!.hasPointerCapture(event.pointerId)) return;
    applyFromPointer(event.clientX);
  };
  const stopDrag = (event: PointerEvent) => {
    if (handle!.hasPointerCapture(event.pointerId)) {
      handle!.releasePointerCapture(event.pointerId);
    }
    dragging = false;
    split.classList.remove('files-browse-split--dragging');
    if (!mq.matches) {
      writeStoredFraction(storageKey, treeFraction);
    }
  };

  handle.addEventListener('pointerdown', onPointerDown);
  handle.addEventListener('pointermove', onPointerMove);
  handle.addEventListener('pointerup', stopDrag);
  handle.addEventListener('pointercancel', stopDrag);

  const onMqChange = () => applyStored();
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', onMqChange);
  } else {
    (mq as MediaQueryList & { addListener: (cb: () => void) => void }).addListener(onMqChange);
  }

  return () => {
    resizeObserver.disconnect();
    handle?.removeEventListener('pointerdown', onPointerDown);
    handle?.removeEventListener('pointermove', onPointerMove);
    handle?.removeEventListener('pointerup', stopDrag);
    handle?.removeEventListener('pointercancel', stopDrag);
    if (typeof mq.removeEventListener === 'function') {
      mq.removeEventListener('change', onMqChange);
    } else {
      (mq as MediaQueryList & { removeListener: (cb: () => void) => void }).removeListener(onMqChange);
    }
    split.classList.remove('files-browse-split--resizable', 'files-browse-split--dragging');
    split.style.gridTemplateColumns = '';
    handle?.remove();
  };
}
